import { ScaleCanvas } from "./ScaleCanvas.js";

/**
 * Amplitude ruler for a waveform lane.
 *
 * The lane draws amplitude linearly about its centre — `y = h * (1 - (a+1)/2)`
 * — so a level in dB sits at `10^(dB/20)` of the half-height, mirrored above and
 * below. Centre is amplitude zero, which is **−∞ dB**: the one label that isn't
 * a number, and the one people look for first.
 *
 * ── Why the ticks don't move with gain ──
 *
 * Per-track gain makes the drawn waveform *taller*; it does not move this ruler.
 * That is the deliberate choice: the ruler is a fixed frame of reference and the
 * picture grows against it, which is how you read a level meter. A ruler that
 * rescaled itself would leave the waveform looking identical at every gain,
 * which is exactly the information the gain control exists to give you.
 *
 * It also means the ruler reads *post-gain* amplitude, which is the honest
 * number — +6 dB of gain on a −12 dB signal genuinely is −6 dB.
 */
export class DbScale extends ScaleCanvas {
  /**
   * Ordered by importance, not by position: `cull` keeps the earliest entries
   * that fit, so a short lane degrades to −∞ and 0 rather than to a jumble.
   */
  static DEFAULT_TICKS = [0, -6, -12, -3, -18, -24];

  constructor(canvas, options = {}) {
    super(canvas, options);
    this.ticks = options.ticks || DbScale.DEFAULT_TICKS;
  }

  /** Distance from the centre line, as a fraction of the half-height. */
  static amplitudeFor(db) {
    return 10 ** (db / 20);
  }

  /**
   * Where a level sits, in CSS pixels down from the top of a lane.
   *
   * @param {number} db - 0 is full scale; `-Infinity` is the centre
   * @param {number} height - lane height
   * @param {number} [sign=1] - +1 for the upper half, -1 for the lower
   */
  static yFor(db, height, sign = 1) {
    const centre = height / 2;
    if (!Number.isFinite(db)) return centre;
    return centre - sign * DbScale.amplitudeFor(db) * centre;
  }

  render(width, height) {
    const theme = this.theme;
    const font = theme.font || "10px sans-serif";
    const gap = theme.labelGap ?? 4;
    const majorLength = theme.majorTickLength ?? 8;
    const minorLength = theme.minorTickLength ?? 5;

    this.ctx.font = font;
    this.ctx.textBaseline = "middle";

    // One line height plus a little, so labels never touch.
    const minimumGap = (theme.minimumLabelGap ?? 1.6) * this.#fontSize(font);

    const candidates = [{ db: -Infinity, y: height / 2, centre: true }];
    for (const db of this.ticks) {
      for (const sign of [1, -1]) {
        candidates.push({ db, y: DbScale.yFor(db, height, sign), centre: false });
      }
    }

    // Anything that would fall outside the lane is not a candidate at all — a
    // tick clamped to the edge would claim a level that isn't drawn there.
    const inside = candidates.filter((c) => c.y >= 0 && c.y <= height);
    const placed = ScaleCanvas.cull(inside, minimumGap);

    for (const tick of placed) {
      const length = tick.centre ? majorLength : minorLength;
      const [x0, x1] = this.tickSpan(width, length);
      const color = tick.centre
        ? theme.centerLineColor || theme.tickColor || "#888"
        : theme.tickColor || "#888";

      this.hairline(x0, x1, tick.y, color);

      const { x, align } = this.labelAnchor(width, length, gap);
      this.ctx.fillStyle = tick.centre
        ? theme.centerTextColor || theme.textColor || "#333"
        : theme.textColor || "#333";
      this.ctx.textAlign = align;
      this.ctx.fillText(DbScale.label(tick.db), x, this.#clampBaseline(tick.y, height, font));
    }
  }

  static label(db) {
    if (!Number.isFinite(db)) return "-∞";
    return db === 0 ? "0" : String(db);
  }

  /** The width this scale would like, so a host can size the element. */
  measure() {
    const theme = this.theme;
    this.ctx.font = theme.font || "10px sans-serif";

    const widest = [-Infinity, ...this.ticks].reduce(
      (max, db) => Math.max(max, this.ctx.measureText(DbScale.label(db)).width),
      0
    );

    return this.widthFor(widest, theme.majorTickLength ?? 8, theme.labelGap ?? 4);
  }

  #fontSize(font) {
    const match = /(\d+(?:\.\d+)?)px/.exec(font);
    return match ? Number(match[1]) : 10;
  }

  /** Keep the top and bottom labels fully on the canvas. */
  #clampBaseline(y, height, font) {
    const half = this.#fontSize(font) / 2;
    return Math.max(half, Math.min(height - half, y));
  }
}
